/// <amd-module name="Repos/PullRequestActions/Components/CompletePullRequestPanel" />
import * as React from "react";
import { GitPullRequestMergeStrategy } from "Repos/Common/Contracts/Git";
import * as Resources from "Repos/PullRequestActions/Resources";
import { CheckEvaluation, ICompletionStatus } from "Repos/Views/PullRequest/Contracts/PullRequest";
import { IPullRequestCompletionOptions } from "Repos/Views/PullRequest/Contracts/PullRequestActions";
import { ISourceRefStatus } from "Repos/Views/PullRequest/PullRequestDataService";
import { getPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import * as PRResources from "Repos/Views/PullRequest/Resources";
import { ignoredManualBuild, updateIgnoreList } from "Repos/Views/PullRequest/Util/Check";
import { WorkItemTransitionWarning } from "Repos/Views/PullRequest/Contracts/WorkItemTransitionWarning";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IRegionComponentProperties, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { Status, StatusSize } from "VSSUI/Status";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { getSafeId, KeyCode } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Link } from "VSSUI/Link";

export interface ICompletePullRequestInitialOptions {
    completeWorkItems: boolean;
    deleteBranch: boolean;
    mergeCommitDescription: string;
    mergeCommitTitle: string;
    mergeStrategy?: GitPullRequestMergeStrategy;
}

export interface ICompletePullRequestPanelProps {
    blockingPolicyMessage?: string;
    canOverridePolicies?: boolean;
    checks: IReadonlyObservableArray<CheckEvaluation>;
    commitCount: number;
    completionStatus: IReadonlyObservableValue<ICompletionStatus>;
    disallowedMergeStrategies: IReadonlyObservableArray<GitPullRequestMergeStrategy>;
    errorMessage: IReadonlyObservableValue<string>;
    hasResolvedConflicts: boolean;
    initialOptions: ICompletePullRequestInitialOptions;
    isAutoComplete: boolean;
    onDismiss: (completionOptions: IPullRequestCompletionOptions | undefined) => void;
    showOverlay: IReadonlyObservableValue<boolean>;
    sourceBranchName: string;
    sourceRefStatus?: ISourceRefStatus;
    warningMessage?: ObservableValue<WorkItemTransitionWarning | undefined>;
}

interface IPullRequestCompletionDetails {
    description: string;
}

type PullRequestCompletionTransformer = (details: IPullRequestCompletionDetails) => Promise<void>;

export class CompletePullRequestPanel extends VssComponent<ICompletePullRequestPanelProps> {
    private cannotDeleteSourceMessage: string | undefined;
    private completeWorkItems: ObservableValue<boolean>;
    private completionDetails: IPullRequestCompletionDetails;
    private deleteSourceBranch: ObservableValue<boolean>;
    private descriptionDisabled = new ObservableValue(false);
    private dropdownItems = new ObservableArray<IListBoxItem<GitPullRequestMergeStrategy>>();
    private dropdownSelection = new DropdownSelection();
    private extensionError = new ObservableValue<string | undefined>(undefined);
    private fieldsValid = new ObservableValue(true);
    private ignoreConfigurationIds = new ObservableArray<number>();
    private transformingDescriptionCount = 0;
    private editCommitMessage = new ObservableValue(false);
    private mergeCommitDescription: ObservableValue<string>;
    private mergeCommitTitle: ObservableValue<string>;
    private mergeStrategy = new ObservableValue(GitPullRequestMergeStrategy.NoFastForward);
    private noRebaseMessage: string | undefined;
    private showOverridePolicyText = new ObservableValue(false);
    private overridePolicyMessage = new ObservableValue("");

    private checkTableColumns: ITableColumn<CheckEvaluation>[] = [
        {
            id: "check",
            renderCell: (rowIndex, columnIndex, tableColumn, item) => {
                const { ignoreConfigurationIds } = this;
                return (
                    <Observer key={rowIndex} {...{ ignoreConfigurationIds }}>
                        {() => {
                            const isIgnoredNow = !item.configurationIds.some(id => ignoreConfigurationIds.value.indexOf(id) === -1);

                            const shouldRequeue = ignoredManualBuild(item.evaluation);
                            const messageId = `pr-panel-checks-row${rowIndex}-col${columnIndex}`;

                            return (
                                <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                                    <div className="flex-row flex-grow flex-center rhythm-horizontal-16 text-ellipsis">
                                        <Checkbox
                                            ariaLabelledBy={messageId}
                                            checked={!isIgnoredNow}
                                            disabled={!item.evaluation}
                                            onChange={(e, shouldBlock) => {
                                                e.preventDefault();

                                                this.ignoreConfigurationIds.value = updateIgnoreList(
                                                    this.ignoreConfigurationIds.value,
                                                    item.configurationIds,
                                                    !shouldBlock
                                                );
                                            }}
                                        />
                                        <Status {...item.statusProps} size={StatusSize.l} />
                                        <div className="flex-column flex-grow">
                                            <div className="font-weight-semibold text-ellipsis" id={getSafeId(messageId)}>
                                                {item.message}
                                            </div>
                                            <div className="secondary-text">
                                                {shouldRequeue && !isIgnoredNow
                                                    ? Resources.WillBeQueuedByAutoComplete
                                                    : item.evaluation
                                                    ? item.statusTitle
                                                    : Resources.CannotWaitForStatusWithoutPolicy}
                                            </div>
                                        </div>
                                    </div>
                                </SimpleTableCell>
                            );
                        }}
                    </Observer>
                );
            },
            width: -100
        }
    ];

    constructor(props: ICompletePullRequestPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.completeWorkItems = new ObservableValue(props.initialOptions.completeWorkItems);
        this.deleteSourceBranch = new ObservableValue(props.initialOptions.deleteBranch);

        // Default to ignoring manual policies which have never been run
        const ignoreByDefault = props.checks.value.filter(c => ignoredManualBuild(c.evaluation));

        this.ignoreConfigurationIds.value = ignoreByDefault.map(c => c.configurationIds).reduce((list, subList) => list.concat(subList), []);

        const sourceRefStatus = props.sourceRefStatus;

        // Determine if rebase merge options are allowed
        if (!sourceRefStatus || sourceRefStatus.isDeleted) {
            this.noRebaseMessage = format(Resources.BranchHasBeenDeleted, props.sourceBranchName);
        } else if (props.hasResolvedConflicts) {
            this.noRebaseMessage = Resources.NoRebaseWithConflicts;
        } else if (
            sourceRefStatus &&
            sourceRefStatus.maxRebaseCommitCount &&
            sourceRefStatus.maxRebaseCommitCount < props.commitCount &&
            !sourceRefStatus.isFastForwardable
        ) {
            this.noRebaseMessage = format(Resources.NoRebaseTooManyCommits, sourceRefStatus.maxRebaseCommitCount);
        }

        const prDetailsService = getPullRequestViewService(context.pageContext);
        this.cannotDeleteSourceMessage = prDetailsService.getSourceDeleteBlockReason();
        if (this.cannotDeleteSourceMessage) {
            this.deleteSourceBranch.value = false;
        }

        this.updateMergeStrategies(props.initialOptions.mergeStrategy);

        this.mergeCommitTitle = new ObservableValue(props.initialOptions.mergeCommitTitle);
        this.completionDetails = { description: props.initialOptions.mergeCommitDescription };
        this.mergeCommitDescription = new ObservableValue(props.initialOptions.mergeCommitDescription);

        this.updateValid();

        this.observe([
            {
                propertyName: "disallowedMergeStrategies",
                observer: () => {
                    this.updateMergeStrategies(this.mergeStrategy.value);
                }
            }
        ]);
    }

    private updateMergeStrategies(mergeStrategy: GitPullRequestMergeStrategy | undefined) {
        this.dropdownItems.value = [
            this.createMergeOption(GitPullRequestMergeStrategy.NoFastForward, PRResources.MergeTypeMerge, Resources.MergeTypeMergeDescription, false),
            this.createMergeOption(
                GitPullRequestMergeStrategy.Squash,
                PRResources.MergeTypeSquashCommit,
                Resources.MergeTypeSquashCommitDescription,
                false
            ),
            this.createMergeOption(GitPullRequestMergeStrategy.Rebase, PRResources.MergeTypeRebase, Resources.MergeTypeRebaseDescription, true),
            this.createMergeOption(
                GitPullRequestMergeStrategy.RebaseMerge,
                PRResources.MergeTypeSemiLinear,
                Resources.MergeTypeSemiLinearDescription,
                true
            )
        ];

        const mergePreference = this.getInitialMergeStrategy(mergeStrategy);
        this.mergeStrategy.value = mergePreference;
        this.dropdownSelection.select(this.dropdownItems.value.findIndex(i => i.data === mergePreference));
    }

    public render() {
        const { isAutoComplete, blockingPolicyMessage, 
            canOverridePolicies, errorMessage, 
            showOverlay, sourceBranchName, warningMessage } = this.props;
        const viewService = getPullRequestViewService(this.context.pageContext);

        return (
            <Observer
                errorMessage={errorMessage}
                extensionError={this.extensionError}
                mergeStrategy={this.mergeStrategy}
                showOverlay={showOverlay}
                valid={this.fieldsValid}
                deleteSourceBranch={this.deleteSourceBranch}
                warningMessage={warningMessage}
                completeWorkItems={this.completeWorkItems}
            >
                {() => {
                    const danger = Boolean(this.needsOverride() && canOverridePolicies);
                    const footerButtons: IButtonProps[] = [
                        {
                            id: "cancel",
                            onClick: () => this.onDismiss(),
                            text: Resources.Cancel
                        },
                        {
                            id: "complete",
                            danger,
                            disabled: !this.fieldsValid.value,
                            onClick: () => this.onComplete(),
                            primary: !danger,
                            text: isAutoComplete
                                ? Resources.SetAutoComplete
                                : this.needsOverride() && canOverridePolicies
                                ? Resources.OverrideAndComplete
                                : Resources.CompleteMerge
                        }
                    ];

                    return (
                        <Panel
                            defaultActiveElement=".strategy-dropdown"
                            footerButtonProps={footerButtons}
                            onDismiss={this.onDismiss}
                            overlayContent={showOverlay.value ? <Spinner /> : undefined}
                            titleProps={{ text: isAutoComplete ? Resources.EnableAutomaticCompletion : Resources.CompletePullRequest }}
                        >
                            <div className="flex-column flex-grow rhythm-vertical-16">
                                {blockingPolicyMessage && (
                                    <MessageCard className="flex-noshrink" severity={MessageCardSeverity.Error}>
                                        {blockingPolicyMessage}
                                    </MessageCard>
                                )}

                                {(warningMessage?.value && this.completeWorkItems.value) && (
                                    <div key="workItemTransitionWarning" className="vc-dialog-merge-warningarea">
                                        <MessageCard severity={MessageCardSeverity.Warning}>
                                            <div style={{"display":"inline"}}>
                                                {warningMessage.value.message}
                                                {warningMessage.value.link &&
                                                <Link
                                                    key="learnMore"
                                                    className="work-item-transition-link"
                                                    href={warningMessage.value.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer">
                                                    {Resources.PullRequest_CompleteMergeDialog_WorkItemTransitionWarningLearnMore}
                                                </Link>}
                                                <br/>
                                                {warningMessage.value.overflowMessage}
                                            </div>
                                        </MessageCard>
                                    </div>
                                )}

                                {errorMessage.value && (
                                    <MessageCard className="flex-noshrink" severity={MessageCardSeverity.Error}>
                                        {errorMessage.value}
                                    </MessageCard>
                                )}

                                {this.extensionError.value && (
                                    <MessageCard className="flex-noshrink" severity={MessageCardSeverity.Error}>
                                        {this.extensionError.value}
                                    </MessageCard>
                                )}

                                {this.mergeStrategy.value === GitPullRequestMergeStrategy.Squash && !this.deleteSourceBranch.value && (
                                    <MessageCard className="flex-noshrink" severity={MessageCardSeverity.Warning}>
                                        {Resources.SquashingButNotDeleting}
                                    </MessageCard>
                                )}

                                <FormItem label={Resources.MergeType}>
                                    <Dropdown
                                        className="strategy-dropdown"
                                        items={this.dropdownItems}
                                        onSelect={(e, item) => {
                                            this.mergeStrategy.value = item.data!;
                                            if (!this.canEditCommitMessage()) {
                                                this.editCommitMessage.value = false;
                                            }

                                            this.updateValid();
                                        }}
                                        selection={this.dropdownSelection}
                                    />
                                </FormItem>

                                <CompletionAnimation strategy={this.mergeStrategy} />

                                <FormItem className="rhythm-vertical-8" label={Resources.PostCompletionOptions}>
                                    <Checkbox
                                        checked={this.completeWorkItems}
                                        label={Resources.CompleteWorkItemsOption}
                                        onChange={(e, val) => (this.completeWorkItems.value = val)}
                                    />
                                    <Checkbox
                                        checked={this.deleteSourceBranch}
                                        disabled={!!this.cannotDeleteSourceMessage}
                                        label={format(Resources.DeleteBranchOption, sourceBranchName)}
                                        onChange={(e, val) => (this.deleteSourceBranch.value = val)}
                                        tabIndex={0}
                                        tooltipProps={this.cannotDeleteSourceMessage ? { text: this.cannotDeleteSourceMessage } : undefined}
                                    />
                                    {this.canEditCommitMessage() ? (
                                        <Checkbox
                                            checked={this.editCommitMessage}
                                            label={Resources.CustomizeMergeCommitMessage}
                                            onChange={(e, val) => {
                                                this.editCommitMessage.value = val;
                                                this.updateValid();
                                            }}
                                        />
                                    ) : (
                                        <MessageCard severity={MessageCardSeverity.Info}>{Resources.RebaseUsesExistingMessages}</MessageCard>
                                    )}
                                    <ConditionalChildren renderChildren={this.editCommitMessage}>
                                        <>
                                            <FormItem className="repos-indented-text-field" error={!this.mergeCommitTitle.value}>
                                                <TextField
                                                    placeholder={Resources.Title}
                                                    onChange={(e, val) => {
                                                        this.mergeCommitTitle.value = val;
                                                        this.updateValid();
                                                    }}
                                                    value={this.mergeCommitTitle}
                                                />
                                            </FormItem>
                                            <Observer descriptionDisabled={this.descriptionDisabled}>
                                                {() => (
                                                    <TextField
                                                        className="repos-indented-text-field"
                                                        disabled={this.descriptionDisabled.value}
                                                        multiline={true}
                                                        placeholder={Resources.Description}
                                                        onChange={(e, val) => (this.mergeCommitDescription.value = val)}
                                                        rows={8}
                                                        value={this.mergeCommitDescription}
                                                    />
                                                )}
                                            </Observer>
                                        </>
                                    </ConditionalChildren>
                                </FormItem>

                                {this.needsOverride() && canOverridePolicies && (
                                    <FormItem key="override" className="rhythm-vertical-8" label={Resources.PolicyOverrideOptions}>
                                        <Checkbox
                                            checked={this.showOverridePolicyText}
                                            label={Resources.OverridePolicyOption}
                                            onChange={(e, val) => {
                                                this.showOverridePolicyText.value = val;
                                                this.updateValid();
                                            }}
                                        />
                                        <Observer showMessage={this.showOverridePolicyText} message={this.overridePolicyMessage}>
                                            {(props: { showMessage: boolean; message: string }) => {
                                                return props.showMessage ? (
                                                    <FormItem
                                                        className="repos-indented-text-field"
                                                        error={props.message ? false : true}
                                                        message={props.message ? undefined : Resources.Required}
                                                    >
                                                        <TextField
                                                            onChange={(e, val) => {
                                                                this.overridePolicyMessage.value = val;
                                                                this.updateValid();
                                                            }}
                                                            placeholder={Resources.OverridePolicyPlaceholder}
                                                            value={props.message}
                                                        />
                                                    </FormItem>
                                                ) : null;
                                            }}
                                        </Observer>
                                    </FormItem>
                                )}

                                {isAutoComplete &&
                                    isFeatureFlagEnabled(this.context.pageContext, "SourceControl.GitPullRequests.SelectiveAutoComplete", false) &&
                                    this.createOptionalChecksSection()}

                                <WrappedComponent<IRegionComponentProperties>
                                    dependencies={["ms.vss-code-web.pr-details-completion-extensions"]}
                                    wrappedType="ms.vss-web.region"
                                    wrappedProps={{
                                        childComponentProperties: {
                                            addCompletionTransformer: async (transform: PullRequestCompletionTransformer) => {
                                                // Once we know there's a transformer, let's disable Complete button and description field
                                                // Thus we avoid overriding any user input).
                                                // This happens early in the panel lifecycle, so it's unlikely that description was changed already.
                                                this.transformingDescriptionCount++;
                                                this.descriptionDisabled.value = true;
                                                this.updateValid();

                                                // We can apply it right now, so no need to save transform for later.
                                                transform(this.completionDetails)
                                                    .catch((error: Error) => {
                                                        // extension is throwing an error, so we dont want to block user from completing
                                                        this.extensionError.value = error.message;
                                                    })
                                                    .then(() => {
                                                        this.mergeCommitDescription.value = this.completionDetails.description;

                                                        this.transformingDescriptionCount--;
                                                        this.descriptionDisabled.value = this.transformingDescriptionCount > 0;
                                                        this.updateValid();
                                                    });
                                            },
                                            pullRequestId: viewService.getId(),
                                            repository: viewService.getTargetRepository()
                                        },
                                        className: "rhythm-vertical-16",
                                        componentRegion: "pullRequestDetailsCompletionExtensions"
                                    }}
                                />
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private createMergeOption(
        strategy: GitPullRequestMergeStrategy,
        name: string,
        description: string,
        isRebase: boolean
    ): IListBoxItem<GitPullRequestMergeStrategy> {
        const id = "merge-option-" + strategy;

        let disallowed = false;
        let disabled = false;
        let message: string | undefined = undefined;

        if (this.noRebaseMessage && isRebase) {
            // Rebase option is not allowed -- bypass doesn't matter
            disallowed = true;
            disabled = true;
            message = this.noRebaseMessage;
        } else {
            disallowed = this.props.disallowedMergeStrategies.value.indexOf(strategy) >= 0;
            if (disallowed) {
                disabled = this.props.isAutoComplete || !this.props.canOverridePolicies;
                message = disabled ? Resources.ForbiddenByPolicy : Resources.MustOverridePolicies;
            }
        }

        return {
            data: strategy,
            disabled,
            id,
            render: () => {
                return (
                    <td className="flex-column padding-8" key={id}>
                        <span className="body-m">{name}</span>
                        <span className="body-s secondary-text">{description}</span>
                        {message && <span className="body-s error-text">{message}</span>}
                    </td>
                );
            },
            text: name
        };
    }

    private createOptionalChecksSection(): JSX.Element {
        const { checks, completionStatus } = this.props;

        return (
            <Observer {...{ checks, completionStatus }}>
                {() => {
                    if (!checks || !checks.length || !completionStatus || !completionStatus.value) {
                        return null;
                    }

                    const optionalChecks = checks.value
                        .filter(check => !check.isRequired)
                        .map(check => ({ ...check, linkProps: undefined }))
                        .sort(
                            // Sort so that "default require" policies appear before "default ignore" (never-run manual builds)
                            (a, b) =>
                                Number(b.configurationIds.some(id => this.ignoreConfigurationIds.value.indexOf(id) === -1)) -
                                Number(a.configurationIds.some(id => this.ignoreConfigurationIds.value.indexOf(id) === -1))
                        );

                    if (!optionalChecks.length) {
                        return null;
                    }

                    return (
                        <Table
                            className="optional-checks-container"
                            columns={this.checkTableColumns}
                            itemProvider={new ArrayItemProvider(optionalChecks)}
                            showHeader={true}
                            renderHeader={this.renderHeader}
                        />
                    );
                }}
            </Observer>
        );
    }

    private renderHeader = (columns: Array<ITableColumn<CheckEvaluation>>): JSX.Element => (
        <thead>
            <tr>
                <td className="separator-line-bottom" colSpan={3}>
                    <div className="flex-column flex-start padding-vertical-8 padding-horizontal-16">
                        <div className="body-m font-weight-semibold">{Resources.RequireAdditionalChecks}</div>
                        <div className="body-s">{Resources.RequireAdditionalChecks_Details}</div>
                    </div>
                </td>
            </tr>
        </thead>
    );

    private updateValid(): void {
        const valid =
            (this.props.isAutoComplete || !this.needsOverride() || !!(this.showOverridePolicyText.value && this.overridePolicyMessage.value)) &&
            !(this.editCommitMessage.value && !this.mergeCommitTitle.value) &&
            this.transformingDescriptionCount === 0;

        if (this.fieldsValid.value !== valid) {
            this.fieldsValid.value = valid;
        }
    }

    private onDismiss = () => {
        this.props.onDismiss(undefined);
    };

    private onComplete = () => {
        const completionOptions: IPullRequestCompletionOptions = {
            completeWorkItems: this.completeWorkItems.value,
            deleteBranch: this.deleteSourceBranch.value,
            mergeCommitDescription: this.editCommitMessage.value ? this.mergeCommitDescription.value : this.completionDetails.description,
            mergeCommitTitle: this.editCommitMessage.value ? this.mergeCommitTitle.value : this.props.initialOptions.mergeCommitTitle,
            mergeStrategy: this.mergeStrategy.value,
            autoCompleteIgnoreConfigIds: [...this.ignoreConfigurationIds.value],
            policyOverrideMessage: this.needsOverride() && this.showOverridePolicyText ? this.overridePolicyMessage.value : undefined
        };
        this.props.onDismiss(completionOptions);
    };

    private needsOverride() {
        return this.props.blockingPolicyMessage || this.props.disallowedMergeStrategies.value.indexOf(this.mergeStrategy.value) >= 0;
    }

    private canEditCommitMessage(): boolean {
        return this.mergeStrategy.value !== GitPullRequestMergeStrategy.Rebase;
    }

    private getInitialMergeStrategy(mergeStrategy: GitPullRequestMergeStrategy | undefined): GitPullRequestMergeStrategy {
        const disallowedStrategies = new Set(this.props.disallowedMergeStrategies.value);
        if (mergeStrategy && !disallowedStrategies.has(mergeStrategy)) {
            return mergeStrategy;
        }

        return !disallowedStrategies.has(GitPullRequestMergeStrategy.NoFastForward)
            ? GitPullRequestMergeStrategy.NoFastForward
            : !disallowedStrategies.has(GitPullRequestMergeStrategy.Squash)
            ? GitPullRequestMergeStrategy.Squash
            : !disallowedStrategies.has(GitPullRequestMergeStrategy.Rebase)
            ? GitPullRequestMergeStrategy.Rebase
            : !disallowedStrategies.has(GitPullRequestMergeStrategy.RebaseMerge)
            ? GitPullRequestMergeStrategy.RebaseMerge
            : GitPullRequestMergeStrategy.NoFastForward;
    }
}

const mergeStrategyImages: { [mergeStrategy: number]: (key: string) => JSX.Element } = {
    [GitPullRequestMergeStrategy.NoFastForward]: key => (
        <svg aria-labelledby="strategyAnimDesc" id="mergeStrategyImage" key={key} viewBox="0 0 140 24" xmlns="http://www.w3.org/2000/svg">
            <desc id="strategyAnimDesc">{Resources.MergeTypeMergeDescription}</desc>
            <rect width="8" height="8" rx="4" className="targetCommit" />
            <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
            <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit" />
            <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit" />
            <rect x="131" y="1" width="6" height="6" rx="4" className="mergeCommit fade-in-merge" />
            <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
            <path d="M54 4H130" className="targetLine grow-right-merge" />
            <line x1="54" y1="20" x2="86" y2="20" className="sourceLine" />
            <path d="M10 4C26 4 26 20 42 20" className="sourceLine" />
            <path d="M98 20C114 20 114 4 130 4" className="sourceLine grow-right-merge" />
        </svg>
    ),
    [GitPullRequestMergeStrategy.Squash]: key => (
        <svg aria-labelledby="strategyAnimDesc" id="mergeStrategyImage" key={key} viewBox="0 0 96 24" xmlns="http://www.w3.org/2000/svg">
            <desc id="strategyAnimDesc">{Resources.MergeTypeSquashCommitDescription}</desc>
            <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
            <line x1="54" y1="20" x2="86" y2="20" className="sourceLineGhost fade-in-final" />
            <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
            <path d="M10 4C26 4 26 20 42 20" className="sourceLineGhost fade-in-final" />
            <rect width="8" height="8" rx="4" className="targetCommit" />
            <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
            <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
            <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-squash" />
            <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit fade-out-squash2" />
            <line x1="54" y1="20" x2="86" y2="20" className="sourceLine shrink-right-squash" />
            <path d="M54 4H86" className="sourceLine grow-right-squash" />
            <path d="M10 4C26 4 26 20 42 20" className="sourceLine fade-out-squash1" />
        </svg>
    ),
    [GitPullRequestMergeStrategy.Rebase]: key => (
        <svg aria-labelledby="strategyAnimDesc" id="mergeStrategyImage" key={key} viewBox="0 0 140 24" xmlns="http://www.w3.org/2000/svg">
            <desc id="strategyAnimDesc">{Resources.MergeTypeRebaseDescription}</desc>
            <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
            <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
            <line x1="54" y1="20" x2="86" y2="20" className="sourceLineGhost fade-in-final" />
            <path d="M10 4C26 4 26 20 42 20" className="sourceLineGhost fade-in-final" />
            <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
            <rect width="8" height="8" rx="4" className="targetCommit" />
            <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
            <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebase" />
            <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebase" />
            <line x1="54" y1="20" x2="86" y2="20" className="sourceLine slide-right-rebase" />
            <path d="M10 4C26 4 26 20 42 20" className="sourceLine fade-out-rebase" />
            <line x1="54" y1="4" x2="86" y2="4" className="sourceLine fade-in-rebase" />
        </svg>
    ),
    [GitPullRequestMergeStrategy.RebaseMerge]: key => (
        <svg aria-labelledby="strategyAnimDesc" id="mergeStrategyImage" key={key} viewBox="0 0 228 24" xmlns="http://www.w3.org/2000/svg">
            <desc id="strategyAnimDesc">{Resources.MergeTypeSemiLinearDescription}</desc>
            <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
            <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
            <line x1="54" y1="20" x2="86" y2="20" className="sourceLineGhost fade-in-final" />
            <path d="M10 4C26 4 26 20 42 20" className="sourceLineGhost fade-in-final" />
            <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
            <line x1="54" y1="4" x2="86" y2="4" className="targetLine" />
            <line x1="98" y1="4" x2="218" y2="4" className="targetLine grow-right-rebaseMerge" />
            <rect width="8" height="8" rx="4" className="targetCommit" />
            <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
            <rect x="88" width="8" height="8" rx="4" className="targetCommit" />
            <rect x="219" y="1" width="6" height="6" rx="4" className="mergeCommit fade-in-rebaseMerge" />
            <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebaseMerge" />
            <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebaseMerge" />
            <line x1="54" y1="20" x2="86" y2="20" className="sourceLine slide-right-rebaseMerge" />
            <path d="M10 4C26 4 26 20 42 20" className="sourceLine slide-right-rebaseMerge" />
            <path d="M142 20C158 20 158 4 174 4" className="sourceLine grow-right-rebaseMerge" />
        </svg>
    )
};

interface ICompletionAnimationProps {
    strategy: IReadonlyObservableValue<GitPullRequestMergeStrategy>;
}

const CompletionAnimation = (props: ICompletionAnimationProps): JSX.Element => {
    const animationIndex = new ObservableValue(0);

    return (
        <Observer strategy={props.strategy} animationIndex={animationIndex}>
            {(props: { strategy: GitPullRequestMergeStrategy; animationIndex: number }) => {
                return (
                    <div
                        aria-labelledby="strategyAnimDesc"
                        className="repos-merge-animation"
                        tabIndex={0}
                        onKeyUp={e => e.keyCode === KeyCode.space && animationIndex.value++}
                        onClick={e => animationIndex.value++}
                        role="figure"
                    >
                        {mergeStrategyImages[props.strategy](`animation-${props.strategy}-${props.animationIndex}`)}
                    </div>
                );
            }}
        </Observer>
    );
};

Components.add("ms.vss-code-web.complete-pull-request-panel", CompletePullRequestPanel);
